import { Report, ReportWithCohorts } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { FilterReportDefinition } from "@/components/reports/filter-report-definition"

import { useApiContext } from "../use-api-context"
import { createReport, deleteReport, runReport, updateReport } from "./api"

export function useCreateReportMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<
    Report,
    Error,
    { name?: string; source_cohort_id?: string; query?: string } | FilterReportDefinition
  >({
    mutationFn: async (params) => {
      const context = await getContext()
      if (!context) throw new Error("No API context available")
      return createReport(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] })
    },
  })
}

export function useUpdateReportMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<ReportWithCohorts, Error, { id: string; definition: { name: string } | FilterReportDefinition }>({
    mutationFn: async ({ id, definition }) => {
      const context = await getContext()
      if (!context) throw new Error("No API context available")
      return updateReport(context, id, definition)
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["reports"] })
      queryClient.invalidateQueries({ queryKey: ["report", id] })
    },
  })
}

export function useDeleteReportMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const context = await getContext()
      if (!context) throw new Error("No API context available")
      return deleteReport(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] })
    },
  })
}

export function useRunReportMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<{ chat_thread_id: string }, Error, { id: string; additionalInstructions?: string }>({
    mutationFn: async ({ id, additionalInstructions }) => {
      const context = await getContext()
      if (!context) throw new Error("No API context available")
      return runReport(context, id, additionalInstructions)
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["reports"] })
      queryClient.invalidateQueries({ queryKey: ["report", id] })
    },
  })
}
